import React, { useContext, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PlaylistDataContext } from '../DataContexts/PlaylistDataContext';
import { MachineContext } from '../DataContexts/MachineContext';
import { MachineGroupContext } from '../DataContexts/MachineGroupContext';
import DashboardSection from '../components/DashboardSection';
import BulkProgress, { useBulkRun, describeBulkSubscription } from '../components/BulkProgress';
import './PlaylistAssign.css';

export const machineDetails = (machine) => ({
  id: machine.id,
  name: machine.name ?? machine.id,
  place: machine.city ?? '-',
  post: machine.postalcode ?? '-',
  province: machine.province ?? '-',
});

export const filterAndSortMachines = (list, search, sortKey, sortAsc) => {
  let result = [...list];
  if (search.trim()) {
    const q = search.toLowerCase().trim();
    result = result.filter(
      (m) =>
        String(m.name ?? '').toLowerCase().includes(q) ||
        String(m.place ?? '').toLowerCase().includes(q) ||
        String(m.post ?? '').toLowerCase().includes(q) ||
        String(m.province ?? '').toLowerCase().includes(q)
    );
  }
  result.sort((a, b) => {
    const cmp = String(a[sortKey] ?? '').localeCompare(String(b[sortKey] ?? ''), undefined, { sensitivity: 'base' });
    return sortAsc ? cmp : -cmp;
  });
  return result;
};

// Reverse-assign section for the Curator > Assign tab: pick a playlist on the
// left, then move machines between subscribed (middle) and available (right).
// `active`/`onToggle` hook the section into the subpage's independent
// collapsed-section logic (DJB-83): the parent tracks which sections are open
// and flips this one on every header interaction.
const PlaylistAssign = ({ active = true, onToggle }) => {
  const { playlists, playlistsLoading, playlistsError } = useContext(PlaylistDataContext);
  const {
    machines,
    loading: machinesLoading,
    error: machinesError,
    toggleMachinePlaylist,
    updatePlaylistPriority,
    showSuccessNotification,
    showErrorNotification,
  } = useContext(MachineContext);
  const { machineGroups, loading: machineGroupsLoading, error: machineGroupsError } = useContext(MachineGroupContext);
  const [, setSearchParams] = useSearchParams();

  const [selectedPlaylistId, setSelectedPlaylistId] = useState(null);
  const [playlistTypeFilter, setPlaylistTypeFilter] = useState('All');
  const [playlistSearch, setPlaylistSearch] = useState('');
  const [machineSearch, setMachineSearch] = useState('');
  const [machineSortKey, setMachineSortKey] = useState('name');
  const [machineSortAsc, setMachineSortAsc] = useState(true);
  const [availableMachineSearch, setAvailableMachineSearch] = useState('');
  const [availableSortKey, setAvailableSortKey] = useState('name');
  const [availableSortAsc, setAvailableSortAsc] = useState(true);
  const [groupSearch, setGroupSearch] = useState('');
  const [confirmRemoveAll, setConfirmRemoveAll] = useState(false);
  // DJB-214: one progress bar + one result toast per bulk run
  const { progress: bulkProgress, running: bulkRunning, run: runBulk } = useBulkRun();

  const selectedPlaylist = useMemo(
    () => playlists.find((p) => p.id === selectedPlaylistId) ?? null,
    [playlists, selectedPlaylistId]
  );

  // "Open in Studio" (DJB-215): deep link into Playlist Studio > Playlist
  // Editor with this playlist selected. The explicit tab param switches the
  // sub-tab (PlaylistHeader) and ?playlistId opens the editor section on that
  // playlist (CuratorPage / PlaylistManager).
  const handleOpenInStudio = (playlistId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'curator');
      next.set('playlistId', playlistId);
      return next;
    });
  };

  const playlistTypes = useMemo(
    () => ['All', ...new Set(playlists.map((p) => p.playlistType))],
    [playlists]
  );

  const filteredPlaylists = useMemo(() => {
    const q = playlistSearch.toLowerCase().trim();
    return playlists.filter(
      (p) =>
        (playlistTypeFilter === 'All' || p.playlistType === playlistTypeFilter) &&
        (!q || p.name?.toLowerCase().includes(q))
    );
  }, [playlists, playlistSearch, playlistTypeFilter]);

  // playlistId -> number of machines subscribed to it (for the badge in the list)
  const subscriberCounts = useMemo(() => {
    const counts = {};
    machines?.forEach((m) => {
      (m.playlists ?? []).forEach((playlistId) => {
        counts[playlistId] = (counts[playlistId] ?? 0) + 1;
      });
    });
    return counts;
  }, [machines]);

  const subscribedMachinesWithDetails = useMemo(() => {
    if (!selectedPlaylistId || !machines?.length) return [];
    return machines
      .filter((m) => m.playlists?.includes(selectedPlaylistId))
      .map((m) => ({
        ...machineDetails(m),
        prioritized: m.playlistPriorities?.[selectedPlaylistId] === true,
      }));
  }, [machines, selectedPlaylistId]);

  const subscribedIdsSet = useMemo(
    () => new Set(subscribedMachinesWithDetails.map((m) => m.id)),
    [subscribedMachinesWithDetails]
  );

  const availableMachinesWithDetails = useMemo(() => {
    if (!selectedPlaylistId || !machines?.length) return [];
    return machines
      .filter((m) => !m.playlists?.includes(selectedPlaylistId))
      .map(machineDetails);
  }, [machines, selectedPlaylistId]);

  const filteredAndSortedMachines = useMemo(
    () => filterAndSortMachines(subscribedMachinesWithDetails, machineSearch, machineSortKey, machineSortAsc),
    [subscribedMachinesWithDetails, machineSearch, machineSortKey, machineSortAsc]
  );

  const filteredAndSortedAvailableMachines = useMemo(
    () => filterAndSortMachines(availableMachinesWithDetails, availableMachineSearch, availableSortKey, availableSortAsc),
    [availableMachinesWithDetails, availableMachineSearch, availableSortKey, availableSortAsc]
  );

  const toggleMachineSort = (key) => {
    if (machineSortKey === key) {
      setMachineSortAsc((a) => !a);
    } else {
      setMachineSortKey(key);
      setMachineSortAsc(true);
    }
  };

  const toggleAvailableSort = (key) => {
    if (availableSortKey === key) {
      setAvailableSortAsc((a) => !a);
    } else {
      setAvailableSortKey(key);
      setAvailableSortAsc(true);
    }
  };

  const handleRemoveMachine = async (machineId) => {
    if (!selectedPlaylistId) return;
    try {
      await toggleMachinePlaylist(machineId, selectedPlaylistId, false);
    } catch (e) {
      // notification already shown in context
    }
  };

  const handleAddMachine = async (machineId) => {
    if (!selectedPlaylistId) return;
    try {
      await toggleMachinePlaylist(machineId, selectedPlaylistId, true);
    } catch (e) {
      // notification already shown in context
    }
  };

  const handleTogglePriority = async (machineId, prioritized) => {
    if (!selectedPlaylistId) return;
    try {
      await updatePlaylistPriority(machineId, selectedPlaylistId, prioritized);
    } catch (e) {
      // notification already shown in context
    }
  };

  // DJB-214: bulk (un)subscribe runs one request per machine with the
  // per-machine toasts suppressed; the progress bar tracks the run and a
  // single green/red toast reports the aggregated result.
  const runBulkSubscription = async (machineIds, isSubscribing) => {
    if (!selectedPlaylistId) return;
    const playlistId = selectedPlaylistId;
    const result = await runBulk({
      label: isSubscribing ? 'Subscribing machines to playlist…' : 'Unsubscribing machines from playlist…',
      items: machineIds,
      action: (machineId) => toggleMachinePlaylist(machineId, playlistId, isSubscribing, { notify: false }),
    });
    if (!result) return;
    const { type, message } = describeBulkSubscription(result, isSubscribing);
    if (type === 'success') {
      showSuccessNotification?.(message);
    } else {
      showErrorNotification?.(message);
    }
  };

  const handleRemoveAllMachines = () => {
    setConfirmRemoveAll(false);
    return runBulkSubscription(subscribedMachinesWithDetails.map((m) => m.id), false);
  };

  const handleAddAllMachines = () =>
    runBulkSubscription(filteredAndSortedAvailableMachines.map((m) => m.id), true);

  const filteredGroups = useMemo(() => {
    if (!machineGroups?.length) return [];
    if (!groupSearch.trim()) return machineGroups;
    const q = groupSearch.toLowerCase().trim();
    return machineGroups.filter((g) => g.name?.toLowerCase().includes(q));
  }, [machineGroups, groupSearch]);

  // skip already-subscribed members so each machine is POSTed at most once
  const handleAddGroup = (group) =>
    runBulkSubscription(
      (group.machineIds ?? []).filter((machineId) => !subscribedIdsSet.has(machineId)),
      true
    );

  const sortArrow = (isActive, asc) =>
    isActive ? <span className="ps-sort-arrow">{asc ? '▲' : '▼'}</span> : null;

  const sectionShell = (children) => (
    <DashboardSection
      id="section-playlist-subscriptions"
      title="Playlist Subscriptions"
      info="Abonneer machines en groepen op playlists en stel hun prioriteit in."
      isOpen={active}
      onToggle={onToggle}
      bodyId="ps-body-subscriptions"
    >
      {/* DJB-214: full-width progress bar above the columns while a bulk run is in flight */}
      <BulkProgress progress={bulkProgress} />
      {children}
    </DashboardSection>
  );

  if (playlistsLoading || machinesLoading || machineGroupsLoading) {
    return sectionShell(<p className="ps-status">Loading data, please wait...</p>);
  }

  if (playlistsError || machinesError || machineGroupsError) {
    return sectionShell(
      <p className="ps-status ps-status--error">
        Error loading data: {playlistsError || machinesError || machineGroupsError}
      </p>
    );
  }

  return sectionShell(
    <div className="ps-columns">
      {/* First column: playlist-type filter, search and list of playlists */}
      <div className="ps-column ps-column--playlists">
        <div className="ps-column-head">
          <span className="ps-column-label">Playlists</span>
          <span className="ps-column-count">{filteredPlaylists.length}</span>
        </div>
        <div className="ps-filters">
          <select
            className="ps-select"
            aria-label="Filter by playlist type"
            value={playlistTypeFilter}
            onChange={(e) => setPlaylistTypeFilter(e.target.value)}
          >
            {playlistTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <input
            type="text"
            className="ps-input"
            placeholder="Search by playlist name..."
            value={playlistSearch}
            onChange={(e) => setPlaylistSearch(e.target.value)}
          />
        </div>
        <div className="ps-list-scroll">
          <ul className="ps-playlist-list">
            {playlists.length === 0 && <li className="ps-empty">No playlists</li>}
            {playlists.length > 0 && filteredPlaylists.length === 0 && (
              <li className="ps-empty">No matching playlists</li>
            )}
            {filteredPlaylists.map((playlist) => (
              <li
                key={playlist.id}
                className={`ps-playlist-item${selectedPlaylistId === playlist.id ? ' selected' : ''}`}
                onClick={() => setSelectedPlaylistId(playlist.id)}
              >
                <div className="ps-playlist-row">
                  <span className="ps-playlist-name">{playlist.name}</span>
                  <span className="ps-subs-badge">{subscriberCounts[playlist.id] ?? 0} subs</span>
                </div>
                <span className="ps-playlist-type">{playlist.playlistType}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {/* Second column: selected playlist info and subscribed machines */}
      <div className="ps-column ps-column--subscribed">
        {selectedPlaylist ? (
          <>
            <div className="ps-selected">
              <div className="ps-selected-row">
                <div className="ps-selected-name">{selectedPlaylist.name}</div>
                <button
                  type="button"
                  className="ps-btn ps-btn--primary"
                  title="Open this playlist in Playlist Studio"
                  onClick={() => handleOpenInStudio(selectedPlaylist.id)}
                >
                  Open in Studio
                </button>
              </div>
              <div className="ps-selected-id">
                Playlist id: <span>{selectedPlaylist.id}</span>
              </div>
            </div>
            <div className="ps-column-head">
              <h3 className="ps-column-label">Subscribed machines</h3>
              <span className="ps-column-count">{subscribedMachinesWithDetails.length}</span>
            </div>
            <div className="ps-toolbar">
              <input
                type="text"
                className="ps-input"
                placeholder="Search by name, place, post, province..."
                value={machineSearch}
                onChange={(e) => setMachineSearch(e.target.value)}
              />
              <button
                type="button"
                className="ps-btn ps-btn--danger"
                onClick={() => setConfirmRemoveAll(true)}
                disabled={subscribedMachinesWithDetails.length === 0 || bulkRunning}
              >
                Remove All
              </button>
            </div>
            {confirmRemoveAll && (
              <div className="ps-confirm-overlay">
                <div className="ps-confirm-dialog">
                  <p>Do you really want to remove all {subscribedMachinesWithDetails.length} machines from this playlist?</p>
                  <div className="ps-confirm-actions">
                    <button
                      type="button"
                      className="ps-btn ps-btn--ghost"
                      onClick={() => setConfirmRemoveAll(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="ps-btn ps-btn--danger-solid"
                      onClick={handleRemoveAllMachines}
                    >
                      Yes, remove all
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="ps-table-scroll">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th className="ps-th-priority">Priority</th>
                    <th className="ps-th-sort" onClick={() => toggleMachineSort('name')}>
                      Name {sortArrow(machineSortKey === 'name', machineSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleMachineSort('place')}>
                      Place {sortArrow(machineSortKey === 'place', machineSortAsc)}
                    </th>
                    <th className="ps-td-action"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedMachines.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="ps-empty-cell">
                        {subscribedMachinesWithDetails.length === 0 ? 'None' : 'No matching machines'}
                      </td>
                    </tr>
                  ) : (
                    filteredAndSortedMachines.map((m) => (
                      <tr key={m.id}>
                        <td>
                          <button
                            type="button"
                            className={`ps-star-btn${m.prioritized ? ' is-on' : ''}`}
                            title={
                              m.prioritized
                                ? 'Remove playlist priority for this machine'
                                : 'Prioritize playlist for this machine'
                            }
                            aria-pressed={m.prioritized}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTogglePriority(m.id, !m.prioritized);
                            }}
                          >
                            {m.prioritized ? '★' : '☆'}
                          </button>
                        </td>
                        <td>{m.name}</td>
                        <td>{m.place}</td>
                        <td className="ps-td-action">
                          <button
                            className="ps-icon-btn ps-icon-btn--remove"
                            title="Remove from playlist"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveMachine(m.id);
                            }}
                          >
                            -
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="ps-placeholder">Select a playlist to see details</div>
        )}
      </div>
      {/* Third column: available machines and machine groups */}
      <div className="ps-column ps-column--available">
        {!selectedPlaylist ? (
          <div className="ps-placeholder">Select a playlist to see details</div>
        ) : (
          <>
            <div className="ps-column-head">
              <h3 className="ps-column-label">Available machines</h3>
              <span className="ps-column-count">{availableMachinesWithDetails.length}</span>
            </div>
            <div className="ps-toolbar">
              <input
                type="text"
                className="ps-input"
                placeholder="Search by name, place, post, province..."
                value={availableMachineSearch}
                onChange={(e) => setAvailableMachineSearch(e.target.value)}
              />
              <button
                type="button"
                className="ps-btn ps-btn--success"
                title="Add all to playlist"
                onClick={handleAddAllMachines}
                disabled={filteredAndSortedAvailableMachines.length === 0 || bulkRunning}
              >
                Add All
              </button>
            </div>
            <div className="ps-table-scroll ps-table-scroll--available">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th className="ps-th-sort" onClick={() => toggleAvailableSort('name')}>
                      Name {sortArrow(availableSortKey === 'name', availableSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleAvailableSort('place')}>
                      Place {sortArrow(availableSortKey === 'place', availableSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleAvailableSort('post')}>
                      Post {sortArrow(availableSortKey === 'post', availableSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleAvailableSort('province')}>
                      Province {sortArrow(availableSortKey === 'province', availableSortAsc)}
                    </th>
                    <th className="ps-td-action"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedAvailableMachines.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="ps-empty-cell">
                        {availableMachinesWithDetails.length === 0
                          ? 'All machines subscribed'
                          : 'No matching machines'}
                      </td>
                    </tr>
                  ) : (
                    filteredAndSortedAvailableMachines.map((m) => (
                      <tr key={m.id}>
                        <td>{m.name}</td>
                        <td>{m.place}</td>
                        <td>{m.post}</td>
                        <td>{m.province}</td>
                        <td className="ps-td-action">
                          <button
                            className="ps-icon-btn ps-icon-btn--add"
                            title="Add to playlist"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddMachine(m.id);
                            }}
                          >
                            +
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="ps-column-head ps-column-head--groups">
              <h3 className="ps-column-label">Machine groups</h3>
              <span className="ps-column-count">{filteredGroups.length}</span>
            </div>
            <div className="ps-toolbar">
              <input
                type="text"
                className="ps-input"
                placeholder="Search by group name..."
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
              />
            </div>
            <div className="ps-table-scroll ps-table-scroll--groups">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Machines</th>
                    <th className="ps-td-action"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGroups.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="ps-empty-cell">
                        {!machineGroups?.length ? 'No groups' : 'No matching groups'}
                      </td>
                    </tr>
                  ) : (
                    filteredGroups.map((group) => (
                      <tr key={group.id}>
                        <td>{group.name}</td>
                        <td>{group.machineIds?.length ?? 0}</td>
                        <td className="ps-td-action">
                          <button
                            className="ps-icon-btn ps-icon-btn--add"
                            title="Add all machines in this group to playlist"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddGroup(group);
                            }}
                            disabled={!group.machineIds?.length || bulkRunning}
                          >
                            +
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PlaylistAssign;
